import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  ChartOptions,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { Calendar, Home, Thermometer, CloudRain, Wind } from 'lucide-react';
import { Device } from '../types';
import analyticsService from '../services/analyticsService';
import {
  AnalyticsContainer,
  DateRangeSelector,
  DateRangeButton,
  DateInputWrapper,
  DateInput,
  ChartsGrid,
  ChartPanel,
  ChartHeader,
  ChartTitle,
  ChartUnit,
  ChartWrapper,
  NoDataMessage
} from '../styles/AnalyticsStyles';
import styled from 'styled-components';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

interface NetatmoAnalyticsDashboardProps {
  mainStation: Device;
  modules: Device[];
}

type TimeRange = '1h' | '1d' | '1w' | '1m' | 'custom';

const ModuleSection = styled.div`
  margin-bottom: 40px;
`;

const ModuleSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  background: rgba(29, 56, 141, 0.15);
  border-left: 3px solid #3b82f6;
  border-radius: 6px;
  margin-bottom: 20px;
`;

const ModuleIcon = styled.div`
  width: 32px;
  height: 32px;
  background: rgba(59, 130, 246, 0.2);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #60a5fa;
`;

const ModuleSectionTitle = styled.h2`
  font-size: 16px;
  font-weight: 500;
  margin: 0;
  color: #e5e7eb;
`;

const NetatmoAnalyticsDashboard: React.FC<NetatmoAnalyticsDashboardProps> = ({ mainStation, modules }) => {
  const [timeRange, setTimeRange] = useState<TimeRange>('1d');
  const [customStartDate, setCustomStartDate] = useState('2024-10-02');
  const [customEndDate, setCustomEndDate] = useState('2025-10-02');
  const [moduleData, setModuleData] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const allDevices = [mainStation, ...modules];

  useEffect(() => {
    fetchAllModuleData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange, customStartDate, customEndDate, mainStation.id, modules]);

  const getDateRange = (): { start: Date; end: Date } => {
    const now = new Date();
    let start = new Date();

    switch (timeRange) {
      case '1h':
        start = new Date(now.getTime() - 60 * 60 * 1000);
        break;
      case '1d':
        start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case '1w':
        start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '1m':
        start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case 'custom':
        start = new Date(customStartDate);
        return { start, end: new Date(customEndDate) };
    }

    return { start, end: now };
  };

  const fetchAllModuleData = async () => {
    try {
      setLoading(true);
      setError(null);

      const { start, end } = getDateRange();
      const dataMap: Record<string, any> = {};

      // Fetch analytics for all modules
      await Promise.all(
        allDevices.map(async (device) => {
          try {
            const response = await analyticsService.getDeviceAnalytics(
              device.id.toString(),
              start,
              end
            );

            if (response.dataPoints.length > 0) {
              dataMap[device.id.toString()] = transformNetatmoData(response.dataPoints, device.deviceType || 'netatmo_weather');
            }
          } catch (err) {
            console.error(`Failed to fetch analytics for device ${device.id}:`, err);
          }
        })
      );

      setModuleData(dataMap);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch analytics data. Please try again.');
      console.error('Error fetching analytics:', err);
      setLoading(false);
    }
  };

  const transformNetatmoData = (dataPoints: any[], deviceType: string) => {
    const parseValue = (val: any): number | null => {
      if (val === null || val === undefined || val === '') return null;
      const parsed = typeof val === 'string' ? parseFloat(val) : val;
      return isNaN(parsed) ? null : parsed;
    };

    const filterValid = (point: { x: Date; y: number | null }) =>
      point.y !== null && point.y !== undefined && !isNaN(point.y);

    const result: any = {
      temperature: dataPoints.map(point => ({
        x: new Date(point.timestamp),
        y: parseValue(point.temperature)
      })).filter(filterValid),
    };

    // Indoor station specific data
    if (deviceType === 'netatmo_weather') {
      result.humidity = dataPoints.map(point => ({
        x: new Date(point.timestamp),
        y: parseValue(point.humidity)
      })).filter(filterValid);

      result.co2 = dataPoints.map(point => ({
        x: new Date(point.timestamp),
        y: parseValue(point.co2)
      })).filter(filterValid);

      result.noise = dataPoints.map(point => ({
        x: new Date(point.timestamp),
        y: parseValue(point.noise)
      })).filter(filterValid);

      result.pressure = dataPoints.map(point => ({
        x: new Date(point.timestamp),
        y: parseValue(point.pressure)
      })).filter(filterValid);

      result.minTemp = dataPoints.map(point => ({
        x: new Date(point.timestamp),
        y: parseValue(point.min_temp)
      })).filter(filterValid);

      result.maxTemp = dataPoints.map(point => ({
        x: new Date(point.timestamp),
        y: parseValue(point.max_temp)
      })).filter(filterValid);
    }

    // Outdoor module specific data
    if (deviceType === 'netatmo_outdoor') {
      result.humidity = dataPoints.map(point => ({
        x: new Date(point.timestamp),
        y: parseValue(point.humidity)
      })).filter(filterValid);

      result.minTemp = dataPoints.map(point => ({
        x: new Date(point.timestamp),
        y: parseValue(point.min_temp)
      })).filter(filterValid);

      result.maxTemp = dataPoints.map(point => ({
        x: new Date(point.timestamp),
        y: parseValue(point.max_temp)
      })).filter(filterValid);
    }

    // Rain gauge specific data
    if (deviceType === 'netatmo_rain') {
      result.rain = dataPoints.map(point => ({
        x: new Date(point.timestamp),
        y: parseValue(point.rain)
      })).filter(filterValid);

      result.rain1h = dataPoints.map(point => ({
        x: new Date(point.timestamp),
        y: parseValue(point.rain_1h)
      })).filter(filterValid);

      result.rain24h = dataPoints.map(point => ({
        x: new Date(point.timestamp),
        y: parseValue(point.rain_24h)
      })).filter(filterValid);
    }

    // Wind gauge specific data
    if (deviceType === 'netatmo_wind') {
      result.windStrength = dataPoints.map(point => ({
        x: new Date(point.timestamp),
        y: parseValue(point.wind_strength)
      })).filter(filterValid);

      result.windAngle = dataPoints.map(point => ({
        x: new Date(point.timestamp),
        y: parseValue(point.wind_angle)
      })).filter(filterValid);

      result.gustStrength = dataPoints.map(point => ({
        x: new Date(point.timestamp),
        y: parseValue(point.gust_strength)
      })).filter(filterValid);

      result.gustAngle = dataPoints.map(point => ({
        x: new Date(point.timestamp),
        y: parseValue(point.gust_angle)
      })).filter(filterValid);
    }

    return result;
  };

  const getChartOptions = (title: string, unit: string): ChartOptions<'line'> => {
    // Determine the appropriate time unit and format based on selected range
    let timeUnit: 'minute' | 'hour' | 'day' | 'week' | 'month' = 'hour';
    let tooltipFormat = 'MMM d, HH:mm';

    // Get the date range for x-axis bounds
    const { start, end } = getDateRange();

    switch (timeRange) {
      case '1h':
        timeUnit = 'minute';
        tooltipFormat = 'MMM d, HH:mm';
        break;
      case '1d':
        timeUnit = 'hour';
        tooltipFormat = 'MMM d, HH:mm';
        break;
      case '1w':
        timeUnit = 'day';
        tooltipFormat = 'MMM d, yyyy';
        break;
      case '1m':
        timeUnit = 'day';
        tooltipFormat = 'MMM d, yyyy';
        break;
      case 'custom':
        // For custom, calculate the range and decide
        const customStart = new Date(customStartDate);
        const customEnd = new Date(customEndDate);
        const daysDiff = (customEnd.getTime() - customStart.getTime()) / (1000 * 60 * 60 * 24);

        if (daysDiff <= 2) {
          timeUnit = 'hour';
          tooltipFormat = 'MMM d, HH:mm';
        } else if (daysDiff <= 31) {
          timeUnit = 'day';
          tooltipFormat = 'MMM d, yyyy';
        } else if (daysDiff <= 90) {
          timeUnit = 'week';
          tooltipFormat = 'MMM d, yyyy';
        } else {
          timeUnit = 'month';
          tooltipFormat = 'MMM yyyy';
        }
        break;
    }

    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        },
        title: {
          display: false
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          backgroundColor: '#1a1a1a',
          titleColor: '#fff',
          bodyColor: '#ccc',
          borderColor: '#404040',
          borderWidth: 1,
          callbacks: {
            title: (context) => {
              const date = new Date(context[0].parsed.x);
              // Format based on time range
              if (timeUnit === 'minute' || timeUnit === 'hour') {
                return date.toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                });
              } else if (timeUnit === 'day' || timeUnit === 'week') {
                return date.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                });
              } else {
                return date.toLocaleDateString('en-US', {
                  month: 'short',
                  year: 'numeric'
                });
              }
            }
          }
        }
      },
      scales: {
        x: {
          type: 'time',
          time: {
            unit: timeUnit,
            displayFormats: {
              minute: 'HH:mm',
              hour: 'HH:mm',
              day: 'MMM d',
              week: 'MMM d',
              month: 'MMM yyyy'
            },
            tooltipFormat: tooltipFormat
          },
          min: start.toISOString(),
          max: end.toISOString(),
          grid: {
            color: '#2a2a2a',
          },
          ticks: {
            color: '#888',
            maxRotation: 0,
            autoSkipPadding: 20,
            source: 'auto',
            autoSkip: true
          }
        },
        y: {
          beginAtZero: false,
          grid: {
            color: '#2a2a2a',
          },
          ticks: {
            color: '#888'
          }
        }
      },
      interaction: {
        mode: 'nearest',
        axis: 'x',
        intersect: false
      }
    };
  };

  const createChartData = (data: any[], label: string, color: string) => ({
    datasets: [
      {
        label,
        data,
        borderColor: color,
        backgroundColor: `${color}20`,
        borderWidth: 2,
        pointRadius: 2,
        pointHoverRadius: 4,
        tension: 0.3,
        fill: true
      }
    ]
  });

  const getModuleIcon = (deviceType: string) => {
    switch (deviceType) {
      case 'netatmo_weather': return <Home size={24} />;
      case 'netatmo_outdoor': return <Thermometer size={24} />;
      case 'netatmo_rain': return <CloudRain size={24} />;
      case 'netatmo_wind': return <Wind size={24} />;
      default: return <Home size={24} />;
    }
  };

  const getModuleName = (deviceType: string) => {
    switch (deviceType) {
      case 'netatmo_weather': return 'Indoor Station';
      case 'netatmo_outdoor': return 'Outdoor Module';
      case 'netatmo_rain': return 'Rain Gauge';
      case 'netatmo_wind': return 'Wind Gauge';
      default: return 'Module';
    }
  };

  const renderModuleCharts = (device: Device) => {
    const data = moduleData[device.id.toString()];
    if (!data) {
      return <NoDataMessage>No data available for this module</NoDataMessage>;
    }

    const deviceType = device.deviceType || 'netatmo_weather';

    return (
      <ChartsGrid>
        {/* Temperature (all modules) */}
        {data.temperature?.length > 0 && (
          <ChartPanel>
            <ChartHeader>
              <ChartTitle>Temperature</ChartTitle>
              <ChartUnit>°C</ChartUnit>
            </ChartHeader>
            <ChartWrapper>
              <Line
                data={createChartData(data.temperature, 'Temperature', '#3b82f6')}
                options={getChartOptions('Temperature', '°C')}
              />
            </ChartWrapper>
          </ChartPanel>
        )}

        {/* Min/Max Temperature */}
        {data.minTemp?.length > 0 && (
          <ChartPanel>
            <ChartHeader>
              <ChartTitle>Min Temperature</ChartTitle>
              <ChartUnit>°C</ChartUnit>
            </ChartHeader>
            <ChartWrapper>
              <Line
                data={createChartData(data.minTemp, 'Min Temp', '#60a5fa')}
                options={getChartOptions('Min Temperature', '°C')}
              />
            </ChartWrapper>
          </ChartPanel>
        )}

        {data.maxTemp?.length > 0 && (
          <ChartPanel>
            <ChartHeader>
              <ChartTitle>Max Temperature</ChartTitle>
              <ChartUnit>°C</ChartUnit>
            </ChartHeader>
            <ChartWrapper>
              <Line
                data={createChartData(data.maxTemp, 'Max Temp', '#ef4444')}
                options={getChartOptions('Max Temperature', '°C')}
              />
            </ChartWrapper>
          </ChartPanel>
        )}

        {/* Indoor station specific */}
        {deviceType === 'netatmo_weather' && (
          <>
            {data.humidity?.length > 0 && (
              <ChartPanel>
                <ChartHeader>
                  <ChartTitle>Humidity</ChartTitle>
                  <ChartUnit>%</ChartUnit>
                </ChartHeader>
                <ChartWrapper>
                  <Line
                    data={createChartData(data.humidity, 'Humidity', '#06b6d4')}
                    options={getChartOptions('Humidity', '%')}
                  />
                </ChartWrapper>
              </ChartPanel>
            )}

            {data.co2?.length > 0 && (
              <ChartPanel>
                <ChartHeader>
                  <ChartTitle>CO₂</ChartTitle>
                  <ChartUnit>ppm</ChartUnit>
                </ChartHeader>
                <ChartWrapper>
                  <Line
                    data={createChartData(data.co2, 'CO₂', '#f59e0b')}
                    options={getChartOptions('CO₂', 'ppm')}
                  />
                </ChartWrapper>
              </ChartPanel>
            )}

            {data.noise?.length > 0 && (
              <ChartPanel>
                <ChartHeader>
                  <ChartTitle>Noise</ChartTitle>
                  <ChartUnit>dB</ChartUnit>
                </ChartHeader>
                <ChartWrapper>
                  <Line
                    data={createChartData(data.noise, 'Noise', '#8b5cf6')}
                    options={getChartOptions('Noise', 'dB')}
                  />
                </ChartWrapper>
              </ChartPanel>
            )}

            {data.pressure?.length > 0 && (
              <ChartPanel>
                <ChartHeader>
                  <ChartTitle>Pressure</ChartTitle>
                  <ChartUnit>mbar</ChartUnit>
                </ChartHeader>
                <ChartWrapper>
                  <Line
                    data={createChartData(data.pressure, 'Pressure', '#10b981')}
                    options={getChartOptions('Pressure', 'mbar')}
                  />
                </ChartWrapper>
              </ChartPanel>
            )}
          </>
        )}

        {/* Outdoor module specific */}
        {deviceType === 'netatmo_outdoor' && data.humidity?.length > 0 && (
          <ChartPanel>
            <ChartHeader>
              <ChartTitle>Humidity</ChartTitle>
              <ChartUnit>%</ChartUnit>
            </ChartHeader>
            <ChartWrapper>
              <Line
                data={createChartData(data.humidity, 'Humidity', '#06b6d4')}
                options={getChartOptions('Humidity', '%')}
              />
            </ChartWrapper>
          </ChartPanel>
        )}

        {/* Rain gauge specific */}
        {deviceType === 'netatmo_rain' && (
          <>
            {data.rain?.length > 0 && (
              <ChartPanel>
                <ChartHeader>
                  <ChartTitle>Rain Rate</ChartTitle>
                  <ChartUnit>mm/h</ChartUnit>
                </ChartHeader>
                <ChartWrapper>
                  <Line
                    data={createChartData(data.rain, 'Rain Rate', '#3b82f6')}
                    options={getChartOptions('Rain Rate', 'mm/h')}
                  />
                </ChartWrapper>
              </ChartPanel>
            )}

            {data.rain1h?.length > 0 && (
              <ChartPanel>
                <ChartHeader>
                  <ChartTitle>Rain (Last Hour)</ChartTitle>
                  <ChartUnit>mm</ChartUnit>
                </ChartHeader>
                <ChartWrapper>
                  <Line
                    data={createChartData(data.rain1h, 'Rain 1h', '#06b6d4')}
                    options={getChartOptions('Rain (Last Hour)', 'mm')}
                  />
                </ChartWrapper>
              </ChartPanel>
            )}

            {data.rain24h?.length > 0 && (
              <ChartPanel>
                <ChartHeader>
                  <ChartTitle>Rain (Last 24 Hours)</ChartTitle>
                  <ChartUnit>mm</ChartUnit>
                </ChartHeader>
                <ChartWrapper>
                  <Line
                    data={createChartData(data.rain24h, 'Rain 24h', '#0ea5e9')}
                    options={getChartOptions('Rain (Last 24 Hours)', 'mm')}
                  />
                </ChartWrapper>
              </ChartPanel>
            )}
          </>
        )}

        {/* Wind gauge specific */}
        {deviceType === 'netatmo_wind' && (
          <>
            {data.windStrength?.length > 0 && (
              <ChartPanel>
                <ChartHeader>
                  <ChartTitle>Wind Speed</ChartTitle>
                  <ChartUnit>km/h</ChartUnit>
                </ChartHeader>
                <ChartWrapper>
                  <Line
                    data={createChartData(data.windStrength, 'Wind Speed', '#10b981')}
                    options={getChartOptions('Wind Speed', 'km/h')}
                  />
                </ChartWrapper>
              </ChartPanel>
            )}

            {data.windAngle?.length > 0 && (
              <ChartPanel>
                <ChartHeader>
                  <ChartTitle>Wind Direction</ChartTitle>
                  <ChartUnit>°</ChartUnit>
                </ChartHeader>
                <ChartWrapper>
                  <Line
                    data={createChartData(data.windAngle, 'Wind Direction', '#14b8a6')}
                    options={getChartOptions('Wind Direction', '°')}
                  />
                </ChartWrapper>
              </ChartPanel>
            )}

            {data.gustStrength?.length > 0 && (
              <ChartPanel>
                <ChartHeader>
                  <ChartTitle>Gust Speed</ChartTitle>
                  <ChartUnit>km/h</ChartUnit>
                </ChartHeader>
                <ChartWrapper>
                  <Line
                    data={createChartData(data.gustStrength, 'Gust Speed', '#f59e0b')}
                    options={getChartOptions('Gust Speed', 'km/h')}
                  />
                </ChartWrapper>
              </ChartPanel>
            )}

            {data.gustAngle?.length > 0 && (
              <ChartPanel>
                <ChartHeader>
                  <ChartTitle>Gust Direction</ChartTitle>
                  <ChartUnit>°</ChartUnit>
                </ChartHeader>
                <ChartWrapper>
                  <Line
                    data={createChartData(data.gustAngle, 'Gust Direction', '#fb923c')}
                    options={getChartOptions('Gust Direction', '°')}
                  />
                </ChartWrapper>
              </ChartPanel>
            )}
          </>
        )}
      </ChartsGrid>
    );
  };

  if (loading) {
    return (
      <AnalyticsContainer>
        <NoDataMessage>Loading analytics data...</NoDataMessage>
      </AnalyticsContainer>
    );
  }

  return (
    <AnalyticsContainer>
      <DateRangeSelector>
        <DateRangeButton
          active={timeRange === '1h'}
          onClick={() => setTimeRange('1h')}
        >
          1 Hour
        </DateRangeButton>
        <DateRangeButton
          active={timeRange === '1d'}
          onClick={() => setTimeRange('1d')}
        >
          1 Day
        </DateRangeButton>
        <DateRangeButton
          active={timeRange === '1w'}
          onClick={() => setTimeRange('1w')}
        >
          1 Week
        </DateRangeButton>
        <DateRangeButton
          active={timeRange === '1m'}
          onClick={() => setTimeRange('1m')}
        >
          1 Month
        </DateRangeButton>
        <DateRangeButton
          active={timeRange === 'custom'}
          onClick={() => setTimeRange('custom')}
        >
          <Calendar size={14} />
          Custom
        </DateRangeButton>
      </DateRangeSelector>

      {timeRange === 'custom' && (
        <DateInputWrapper>
          <DateInput
            type="date"
            value={customStartDate}
            onChange={(e) => setCustomStartDate(e.target.value)}
          />
          <span style={{ color: '#888' }}>to</span>
          <DateInput
            type="date"
            value={customEndDate}
            onChange={(e) => setCustomEndDate(e.target.value)}
          />
        </DateInputWrapper>
      )}

      {error && <NoDataMessage>{error}</NoDataMessage>}

      {/* Render charts for each module */}
      {allDevices.map((device) => (
        <ModuleSection key={device.id}>
          <ModuleSectionHeader>
            <ModuleIcon>
              {getModuleIcon(device.deviceType || 'netatmo_weather')}
            </ModuleIcon>
            <ModuleSectionTitle>
              {device.name} - {getModuleName(device.deviceType || 'netatmo_weather')}
            </ModuleSectionTitle>
          </ModuleSectionHeader>
          {renderModuleCharts(device)}
        </ModuleSection>
      ))}
    </AnalyticsContainer>
  );
};

export default NetatmoAnalyticsDashboard;

